import React, { useState } from 'react';
import { FileCheck, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import { LTSSService, StaffMetrics, Alert, ClientUtilization, FinancialSummary } from '../../types/ltss';

interface AuditReportData {
  fileName: string;
  services: LTSSService[];
  alerts: Alert[];
  staffMetrics: StaffMetrics[];
  clientUtilization: ClientUtilization[];
  financials: FinancialSummary;
  reportPeriod?: { start: Date; end: Date };
}

interface AuditReportButtonProps {
  data: AuditReportData;
}

export function AuditReportButton({ data }: AuditReportButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateAuditReport = async () => {
    setIsGenerating(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let y = 20;
      let pageNum = 1;

      // ============================================
      // HELPER FUNCTIONS
      // ============================================
      
      const newPage = () => {
        addFooter();
        pdf.addPage();
        pageNum++;
        y = 25;
        addPageHeader();
      };

      const addPageHeader = () => {
        pdf.setFillColor(30, 41, 59);
        pdf.rect(0, 0, pageWidth, 15, 'F');
        pdf.setFontSize(8);
        pdf.setFont('times', 'normal');
        pdf.setTextColor(200, 200, 200);
        pdf.text('LTSS Analytics - Compliance & Operations Report', margin, 10);
        pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 60, 10);
        pdf.setTextColor(0, 0, 0);
      };

      const addFooter = () => {
        pdf.setFontSize(8);
        pdf.setFont('times', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${pageNum}`, pageWidth / 2 - 10, pageHeight - 10);
        pdf.text('Confidential - For Internal Use Only', margin, pageHeight - 10);
        pdf.setTextColor(0, 0, 0);
      };

      const checkPageBreak = (needed: number = 20) => {
        if (y > pageHeight - 30 - needed) {
          newPage();
        }
      };

      const addSectionHeader = (num: string, text: string) => {
        checkPageBreak(25);
        y += 5;
        pdf.setFillColor(240, 240, 245);
        pdf.rect(margin - 2, y - 5, contentWidth + 4, 10, 'F');
        pdf.setFontSize(12);
        pdf.setFont('times', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(`${num}. ${text}`, margin, y);
        pdf.setTextColor(0, 0, 0);
        y += 10;
      };

      const addSubSection = (text: string) => {
        checkPageBreak(15);
        y += 3;
        pdf.setFontSize(10);
        pdf.setFont('times', 'bold');
        pdf.setTextColor(60, 60, 60);
        pdf.text(text, margin, y);
        pdf.setTextColor(0, 0, 0);
        y += 6;
      };

      const addParagraph = (text: string, indent: number = 0) => {
        checkPageBreak(8);
        pdf.setFontSize(9);
        pdf.setFont('times', 'normal');
        const lines = pdf.splitTextToSize(text, contentWidth - indent);
        lines.forEach((line: string) => {
          checkPageBreak(5);
          pdf.text(line, margin + indent, y);
          y += 4.5;
        });
      };

      const addBullet = (text: string, indent: number = 5) => {
        checkPageBreak(6);
        pdf.setFontSize(9);
        pdf.setFont('times', 'normal');
        pdf.text('•', margin + indent, y);
        const lines = pdf.splitTextToSize(text, contentWidth - indent - 8);
        lines.forEach((line: string, i: number) => {
          if (i > 0) checkPageBreak(5);
          pdf.text(line, margin + indent + 5, y);
          y += 4.5;
        });
      };

      const addTableRow = (cols: string[], widths: number[], bold: boolean = false, bgColor?: number[]) => {
        checkPageBreak(7);
        if (bgColor) {
          pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          pdf.rect(margin, y - 4, contentWidth, 6, 'F');
        }
        pdf.setFontSize(8);
        pdf.setFont('times', bold ? 'bold' : 'normal');
        let x = margin;
        cols.forEach((col, i) => {
          // Calculate max characters that fit in column width (approx 2.5 chars per mm at font size 8)
          const maxChars = Math.floor(widths[i] / 2.2);
          const text = col.length > maxChars ? col.substring(0, maxChars - 2) + '..' : col;
          pdf.text(text, x + 1, y);
          x += widths[i];
        });
        y += 5;
      };

      const addMetricBox = (label: string, value: string, status: 'good' | 'warning' | 'critical' | 'neutral') => {
        const colors = {
          good: [220, 252, 231],
          warning: [254, 249, 195],
          critical: [254, 226, 226],
          neutral: [241, 245, 249]
        };
        const textColors = {
          good: [22, 101, 52],
          warning: [133, 77, 14],
          critical: [153, 27, 27],
          neutral: [51, 65, 85]
        };
        
        pdf.setFillColor(colors[status][0], colors[status][1], colors[status][2]);
        pdf.roundedRect(margin, y, 55, 18, 2, 2, 'F');
        
        pdf.setFontSize(8);
        pdf.setFont('times', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(label, margin + 3, y + 5);
        
        pdf.setFontSize(14);
        pdf.setFont('times', 'bold');
        pdf.setTextColor(textColors[status][0], textColors[status][1], textColors[status][2]);
        pdf.text(value, margin + 3, y + 14);
        pdf.setTextColor(0, 0, 0);
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const addLine = () => {
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 3;
      };

      // ============================================
      // TITLE PAGE
      // ============================================
      pdf.setFillColor(30, 41, 59);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Logo area
      pdf.setFillColor(59, 130, 246);
      pdf.circle(pageWidth / 2, 60, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont('times', 'bold');
      pdf.text('LTSS', pageWidth / 2, 57, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Analytics', pageWidth / 2, 65, { align: 'center' });
      
      // Title
      pdf.setFontSize(28);
      pdf.setFont('times', 'bold');
      pdf.text('Compliance & Operations', pageWidth / 2, 100, { align: 'center' });
      pdf.text('Audit Report', pageWidth / 2, 112, { align: 'center' });
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setFont('times', 'normal');
      pdf.setTextColor(180, 180, 180);
      pdf.text('Long-Term Services and Supports Analysis', pageWidth / 2, 130, { align: 'center' });
      pdf.text('Maryland HCBS Program', pageWidth / 2, 140, { align: 'center' });
      
      // Report info box
      pdf.setFillColor(45, 55, 72);
      pdf.roundedRect(40, 160, pageWidth - 80, 50, 3, 3, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.text(`Report Date: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, 50, 175);
      pdf.text(`Source File: ${data.fileName}`, 50, 185);
      pdf.text(`Total Services Analyzed: ${data.services.length.toLocaleString()}`, 50, 195);
      pdf.text(`Report ID: RPT-${Date.now().toString(36).toUpperCase()}`, 50, 205);
      
      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text('This report is confidential and intended for authorized personnel only.', pageWidth / 2, 260, { align: 'center' });
      pdf.text('Data processed locally - No PHI transmitted to external servers.', pageWidth / 2, 268, { align: 'center' });
      
      // ============================================
      // TABLE OF CONTENTS
      // ============================================
      newPage();
      pdf.setFontSize(16);
      pdf.setFont('times', 'bold');
      pdf.text('Table of Contents', margin, y);
      y += 15;

      const tocItems = [
        ['1', 'Executive Summary', '3'],
        ['2', 'EVV Compliance Analysis', '4'],
        ['3', 'Staff Performance & Manual Entry Review', '5'],
        ['4', 'Service Time Verification', '7'],
        ['5', 'Client Authorization Compliance', '9'],
        ['6', 'Billing & Revenue Analysis', '11'],
        ['7', 'Complete Staff Roster with Metrics', '13'],
        ['8', 'Complete Client Roster with Utilization', '15'],
        ['9', 'All Alerts & Issues Detail', '17'],
        ['10', 'Audit Findings & Required Actions', '19'],
        ['11', 'Recommendations for Improvement', '21'],
      ];

      pdf.setFontSize(10);
      tocItems.forEach(([num, title, page]) => {
        pdf.setFont('times', 'bold');
        pdf.text(num + '.', margin, y);
        pdf.setFont('times', 'normal');
        pdf.text(title, margin + 10, y);
        pdf.text(page, pageWidth - margin - 10, y);
        // Dotted line
        const titleWidth = pdf.getTextWidth(title);
        const dotsStart = margin + 12 + titleWidth;
        const dotsEnd = pageWidth - margin - 15;
        for (let x = dotsStart; x < dotsEnd; x += 3) {
          pdf.text('.', x, y);
        }
        y += 7;
      });

      // ============================================
      // SECTION 1: EXECUTIVE SUMMARY
      // ============================================
      newPage();
      addSectionHeader('1', 'EXECUTIVE SUMMARY');
      
      addParagraph('This report provides a comprehensive analysis of LTSS (Long-Term Services and Supports) operations, compliance status, and areas requiring attention. It is designed to serve multiple purposes:');
      y += 3;
      addBullet('Prepare for Maryland Medicaid audits and EVV compliance reviews');
      addBullet('Identify operational inefficiencies and revenue leakage');
      addBullet('Monitor staff performance and training needs');
      addBullet('Track client service utilization against authorizations');
      addBullet('Detect potential billing errors or fraud indicators');
      y += 5;

      // Key Metrics Summary
      addSubSection('Key Performance Indicators');
      
      // Calculate all metrics
      const manualEntries = data.services.filter(s => 
        s.serviceInitiationSource?.toLowerCase().includes('manual') ||
        s.serviceInitiationSource?.toLowerCase().includes('provider')
      );
      const evvCompliant = data.services.filter(s => 
        s.serviceInitiationSource?.toLowerCase().includes('evv') ||
        s.serviceInitiationSource?.toLowerCase().includes('mobile') ||
        s.serviceInitiationSource?.toLowerCase().includes('gps') ||
        s.serviceInitiationSource?.toLowerCase().includes('telephony')
      );
      const evvRate = data.services.length > 0 ? (evvCompliant.length / data.services.length) * 100 : 0;
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const staffWithIssues = data.staffMetrics.filter(s => s.penaltyStatus !== 'ok');
      const overUtilized = data.clientUtilization.filter(c => c.utilizationRate > 100);
      
      // Duplicate check
      const duplicateCheck: Record<string, number> = {};
      data.services.forEach(s => {
        if (s.staffName && s.serviceDate && s.startTime) {
          const key = `${s.staffName}-${s.serviceDate}-${s.startTime}`;
          duplicateCheck[key] = (duplicateCheck[key] || 0) + 1;
        }
      });
      const duplicates = Object.entries(duplicateCheck).filter(([_, count]) => count > 1);

      // Display metric boxes
      addMetricBox('EVV Compliance', `${evvRate.toFixed(1)}%`, evvRate >= 90 ? 'good' : evvRate >= 70 ? 'warning' : 'critical');
      
      const oldY = y;
      y = oldY;
      pdf.setFillColor(evvRate >= 90 ? 220 : evvRate >= 70 ? 254 : 254, evvRate >= 90 ? 252 : evvRate >= 70 ? 249 : 226, evvRate >= 90 ? 231 : evvRate >= 70 ? 195 : 226);
      pdf.roundedRect(margin + 60, y, 55, 18, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Manual Entries', margin + 63, y + 5);
      pdf.setFontSize(14);
      pdf.setFont('times', 'bold');
      pdf.setTextColor(manualEntries.length > 10 ? 153 : 51, manualEntries.length > 10 ? 27 : 65, manualEntries.length > 10 ? 27 : 85);
      pdf.text(manualEntries.length.toString(), margin + 63, y + 14);
      
      y = oldY;
      pdf.setFillColor(duplicates.length === 0 ? 220 : 254, duplicates.length === 0 ? 252 : 226, duplicates.length === 0 ? 231 : 226);
      pdf.roundedRect(margin + 120, y, 55, 18, 2, 2, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Duplicate Entries', margin + 123, y + 5);
      pdf.setFontSize(14);
      pdf.setFont('times', 'bold');
      pdf.setTextColor(duplicates.length > 0 ? 153 : 22, duplicates.length > 0 ? 27 : 101, duplicates.length > 0 ? 27 : 52);
      pdf.text(duplicates.length.toString(), margin + 123, y + 14);
      
      y += 25;
      pdf.setTextColor(0, 0, 0);

      // Summary stats
      addSubSection('Report Period Statistics');
      const stats = [
        ['Total Services', data.services.length.toLocaleString()],
        ['Total Staff Members', data.staffMetrics.length.toString()],
        ['Total Clients', data.clientUtilization.length.toString()],
        ['Total Revenue', `$${data.financials.totalAmountPaid.toLocaleString()}`],
        ['Total Alerts', data.alerts.length.toString()],
        ['Critical Alerts', data.alerts.filter(a => a.type === 'CRITICAL').length.toString()],
      ];
      
      stats.forEach(([label, value]) => {
        addBullet(`${label}: ${value}`);
      });

      // ============================================
      // SECTION 2: EVV COMPLIANCE ANALYSIS
      // ============================================
      newPage();
      addSectionHeader('2', 'EVV COMPLIANCE ANALYSIS');
      
      addParagraph('Electronic Visit Verification (EVV) is mandated by the 21st Century Cures Act and Maryland COMAR 10.09.36.06 for all personal care services. This section analyzes compliance with EVV requirements.');
      y += 5;

      addSubSection('Compliance Overview');
      
      const evvStats = [
        ['Services with Valid EVV', evvCompliant.length, ((evvCompliant.length / data.services.length) * 100).toFixed(1) + '%'],
        ['Manual/Provider Entries', manualEntries.length, ((manualEntries.length / data.services.length) * 100).toFixed(1) + '%'],
        ['Missing EVV Data', data.services.length - evvCompliant.length - manualEntries.length, ((data.services.length - evvCompliant.length - manualEntries.length) / data.services.length * 100).toFixed(1) + '%'],
      ];

      // Table
      addTableRow(['Category', 'Count', 'Percentage'], [80, 50, 50], true, [240, 240, 245]);
      evvStats.forEach(([cat, count, pct]) => {
        addTableRow([cat as string, count.toString(), pct as string], [80, 50, 50]);
      });
      y += 5;

      // EVV by initiation source
      addSubSection('Services by Initiation Source');
      const sourceGroups: Record<string, number> = {};
      data.services.forEach(s => {
        const source = s.serviceInitiationSource || 'Not Specified';
        sourceGroups[source] = (sourceGroups[source] || 0) + 1;
      });

      addTableRow(['Initiation Source', 'Count', 'Percentage'], [120, 30, 30], true, [240, 240, 245]);
      Object.entries(sourceGroups)
        .sort((a, b) => b[1] - a[1])
        .forEach(([source, count]) => {
          // Truncate long source names to prevent overlap
          const displaySource = source.length > 40 ? source.substring(0, 37) + '...' : source;
          addTableRow([displaySource, count.toString(), ((count / data.services.length) * 100).toFixed(1) + '%'], [120, 30, 30]);
        });

      y += 5;
      addParagraph('Note: Maryland requires 90% EVV compliance. Services initiated via "Provider Manual" or similar bypass the EVV requirement and may be flagged during audits.');

      // ============================================
      // SECTION 3: STAFF PERFORMANCE & MANUAL ENTRIES
      // ============================================
      newPage();
      addSectionHeader('3', 'STAFF PERFORMANCE & MANUAL ENTRY REVIEW');
      
      addParagraph('This section identifies staff members who have used manual entry methods instead of EVV. High manual entry rates may indicate training needs, technical issues, or potential compliance concerns.');
      y += 5;

      // Group manual entries by staff
      const manualByStaff: Record<string, { count: number; dates: Set<string>; services: LTSSService[] }> = {};
      manualEntries.forEach(s => {
        const staff = s.staffName || 'Unknown';
        if (!manualByStaff[staff]) manualByStaff[staff] = { count: 0, dates: new Set(), services: [] };
        manualByStaff[staff].count++;
        manualByStaff[staff].services.push(s);
        if (s.serviceDate) {
          manualByStaff[staff].dates.add(new Date(s.serviceDate).toLocaleDateString());
        }
      });

      const sortedManualStaff = Object.entries(manualByStaff).sort((a, b) => b[1].count - a[1].count);

      if (sortedManualStaff.length > 0) {
        addSubSection('Staff with Manual Entries (Complete List)');
        
        addTableRow(['Staff Name', 'Manual Count', '% of Services', 'Dates Affected'], [60, 30, 35, 55], true, [240, 240, 245]);
        
        sortedManualStaff.forEach(([staffName, info]) => {
          const staffMetric = data.staffMetrics.find(s => s.staffName === staffName);
          const totalServices = staffMetric?.totalServices || info.count;
          const pct = ((info.count / totalServices) * 100).toFixed(0);
          const dates = Array.from(info.dates).slice(0, 3).join(', ') + (info.dates.size > 3 ? `... (+${info.dates.size - 3} more)` : '');
          
          addTableRow([staffName.substring(0, 30), info.count.toString(), pct + '%', dates], [60, 30, 35, 55]);
        });

        y += 5;

        // Detailed breakdown for high-risk staff
        const highRiskStaff = sortedManualStaff.filter(([_, info]) => info.count >= 3);
        if (highRiskStaff.length > 0) {
          addSubSection('Detailed Review: Staff with 3+ Manual Entries');
          
          highRiskStaff.forEach(([staffName, info]) => {
            checkPageBreak(30);
            pdf.setFontSize(9);
            pdf.setFont('times', 'bold');
            pdf.text(`${staffName} - ${info.count} manual entries`, margin, y);
            y += 5;
            
            pdf.setFont('times', 'normal');
            info.services.slice(0, 10).forEach(service => {
              checkPageBreak(5);
              const date = service.serviceDate ? new Date(service.serviceDate).toLocaleDateString() : 'N/A';
              const client = service.clientName || 'Unknown Client';
              const time = service.startTime || 'N/A';
              pdf.text(`   ${date} - ${client} - Start: ${time}`, margin, y);
              y += 4;
            });
            if (info.services.length > 10) {
              pdf.text(`   ... and ${info.services.length - 10} more entries`, margin, y);
              y += 4;
            }
            y += 3;
          });
        }
      } else {
        addParagraph('No manual entries found. All services used proper EVV methods.');
      }

      // Staff performance summary
      newPage();
      addSubSection('Staff Performance Summary');
      
      addTableRow(['Staff Name', 'Total Svc', 'Manual', 'Clean Rate', 'Status'], [55, 25, 25, 30, 45], true, [240, 240, 245]);
      
      // Sort by penalty status (exceeded first, then risk, then ok)
      const sortedStaff = [...data.staffMetrics].sort((a, b) => {
        const order = { exceeded: 0, risk: 1, ok: 2 };
        return order[a.penaltyStatus] - order[b.penaltyStatus];
      });

      sortedStaff.forEach(staff => {
        const statusText = staff.penaltyStatus === 'exceeded' ? 'EXCEEDED - ACTION REQ' : 
                          staff.penaltyStatus === 'risk' ? 'AT RISK' : 'Compliant';
        addTableRow([
          staff.staffName.substring(0, 28),
          staff.totalServices.toString(),
          staff.manualCorrections.toString(),
          staff.cleanRate.toFixed(0) + '%',
          statusText
        ], [55, 25, 25, 30, 45]);
      });

      // ============================================
      // SECTION 4: SERVICE TIME VERIFICATION
      // ============================================
      newPage();
      addSectionHeader('4', 'SERVICE TIME VERIFICATION');
      
      addParagraph('Accurate service time documentation is critical for billing compliance and audit readiness. This section identifies services with time-related issues.');
      y += 5;

      const servicesWithTime = data.services.filter(s => s.startTime && s.endTime);
      const servicesMissingTime = data.services.filter(s => !s.startTime || !s.endTime);
      const overnightServices = data.services.filter(s => {
        if (!s.startTime || !s.endTime) return false;
        const start = parseInt(s.startTime.replace(':', ''));
        const end = parseInt(s.endTime.replace(':', ''));
        return end < start;
      });

      addSubSection('Time Documentation Summary');
      addBullet(`Services with complete time records: ${servicesWithTime.length.toLocaleString()} (${((servicesWithTime.length / data.services.length) * 100).toFixed(1)}%)`);
      addBullet(`Services missing start or end time: ${servicesMissingTime.length.toLocaleString()}`);
      addBullet(`Overnight services (end time before start): ${overnightServices.length.toLocaleString()}`);
      addBullet(`Potential duplicate clock-ins: ${duplicates.length.toLocaleString()}`);
      y += 5;

      // Duplicate details
      if (duplicates.length > 0) {
        addSubSection('Duplicate Clock-in Details (REQUIRES INVESTIGATION)');
        addParagraph('The following entries show the same staff member clocking in at the same time on the same date. This may indicate system errors, double-billing, or fraudulent entries.');
        y += 3;

        addTableRow(['Staff Name', 'Date', 'Start Time', 'Occurrences'], [60, 45, 35, 40], true, [254, 226, 226]);
        
        duplicates.forEach(([key, count]) => {
          const parts = key.split('-');
          const staff = parts[0] || 'Unknown';
          const date = parts[1] || 'N/A';
          const time = parts[2] || 'N/A';
          addTableRow([staff.substring(0, 30), date, time, count.toString()], [60, 45, 35, 40]);
        });
      }

      // Missing time details
      if (servicesMissingTime.length > 0) {
        y += 5;
        addSubSection('Services Missing Time Records');
        
        addTableRow(['Staff Name', 'Client', 'Date', 'Issue'], [50, 50, 40, 40], true, [254, 249, 195]);
        
        servicesMissingTime.slice(0, 50).forEach(service => {
          const issue = !service.startTime && !service.endTime ? 'Both missing' : 
                       !service.startTime ? 'No start time' : 'No end time';
          addTableRow([
            (service.staffName || 'Unknown').substring(0, 25),
            (service.clientName || 'Unknown').substring(0, 25),
            service.serviceDate ? new Date(service.serviceDate).toLocaleDateString() : 'N/A',
            issue
          ], [50, 50, 40, 40]);
        });
        
        if (servicesMissingTime.length > 50) {
          addParagraph(`... and ${servicesMissingTime.length - 50} more services with missing time records.`);
        }
      }

      // ============================================
      // SECTION 5: CLIENT AUTHORIZATION COMPLIANCE
      // ============================================
      newPage();
      addSectionHeader('5', 'CLIENT AUTHORIZATION COMPLIANCE');
      
      addParagraph('This section compares actual service hours against authorized hours for each client. Over-utilization may result in claim denials, while under-utilization may indicate service delivery issues.');
      y += 5;

      const severelyUnder = data.clientUtilization.filter(c => c.utilizationRate < 30);
      const underUtilized = data.clientUtilization.filter(c => c.utilizationRate >= 30 && c.utilizationRate < 50);
      const normalUtil = data.clientUtilization.filter(c => c.utilizationRate >= 50 && c.utilizationRate <= 100);

      addSubSection('Utilization Summary');
      addBullet(`Over-utilized (>100%): ${overUtilized.length} clients - REQUIRES IMMEDIATE REVIEW`);
      addBullet(`Normal utilization (50-100%): ${normalUtil.length} clients`);
      addBullet(`Under-utilized (30-50%): ${underUtilized.length} clients`);
      addBullet(`Severely under-utilized (<30%): ${severelyUnder.length} clients - May need outreach`);
      y += 5;

      // Over-utilized clients (CRITICAL)
      if (overUtilized.length > 0) {
        addSubSection('CRITICAL: Clients Exceeding Authorization');
        addParagraph('These clients have received more services than authorized. Review for authorization updates or billing adjustments.');
        y += 3;

        addTableRow(['Client Name', 'Program', 'Auth Hours', 'Used Hours', 'Utilization', 'Variance'], [45, 25, 25, 25, 30, 30], true, [254, 226, 226]);
        
        overUtilized.forEach(client => {
          addTableRow([
            client.clientName.substring(0, 22),
            client.program || 'N/A',
            client.authorizedHours.toFixed(1),
            client.usedHours.toFixed(1),
            client.utilizationRate.toFixed(0) + '%',
            '+' + (client.usedHours - client.authorizedHours).toFixed(1) + 'h'
          ], [45, 25, 25, 25, 30, 30]);
        });
      }

      // Under-utilized clients
      if (severelyUnder.length > 0) {
        y += 5;
        addSubSection('Severely Under-Utilized Clients (<30%)');
        addParagraph('These clients are using less than 30% of authorized hours. Consider outreach to ensure services are being delivered as needed.');
        y += 3;

        addTableRow(['Client Name', 'Program', 'Auth Hours', 'Used Hours', 'Utilization'], [55, 30, 30, 30, 35], true, [254, 249, 195]);
        
        severelyUnder.forEach(client => {
          addTableRow([
            client.clientName.substring(0, 28),
            client.program || 'N/A',
            client.authorizedHours.toFixed(1),
            client.usedHours.toFixed(1),
            client.utilizationRate.toFixed(0) + '%'
          ], [55, 30, 30, 30, 35]);
        });
      }

      // ============================================
      // SECTION 6: BILLING & REVENUE ANALYSIS
      // ============================================
      newPage();
      addSectionHeader('6', 'BILLING & REVENUE ANALYSIS');
      
      addParagraph('This section analyzes billing patterns, claim status, and potential revenue leakage. Identifying billing issues early prevents claim denials and revenue loss.');
      y += 5;

      addSubSection('Revenue Summary');
      addBullet(`Total Amount Billed: $${data.financials.totalAmountPaid.toLocaleString()}`);
      addBullet(`Total Units Billed: ${data.financials.totalUnitsBilled.toLocaleString()}`);
      addBullet(`Claims Paid: ${data.financials.claimsPaid.toLocaleString()}`);
      addBullet(`Claims Pending: ${data.financials.claimsPending.toLocaleString()}`);
      addBullet(`Claims Not Submitted: ${data.financials.claimsNotSubmitted.toLocaleString()}`);
      addBullet(`Claims Voided: ${data.financials.claimsVoided.toLocaleString()}`);
      addBullet(`Estimated Revenue Leakage: $${data.financials.estimatedLeakage.toLocaleString()}`);
      y += 5;

      // Claims by status
      const claimStatusCounts: Record<string, number> = {};
      data.services.forEach(s => {
        const status = s.claimHistory || 'Not Specified';
        claimStatusCounts[status] = (claimStatusCounts[status] || 0) + 1;
      });

      addSubSection('Claims by Status');
      addTableRow(['Claim Status', 'Count', 'Percentage', 'Action Needed'], [60, 35, 35, 50], true, [240, 240, 245]);
      
      Object.entries(claimStatusCounts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([status, count]) => {
          const pct = ((count / data.services.length) * 100).toFixed(1);
          let action = 'None';
          if (status.toLowerCase().includes('pending')) action = 'Follow up';
          if (status.toLowerCase().includes('denied') || status.toLowerCase().includes('void')) action = 'Review & Resubmit';
          if (status.toLowerCase().includes('not submitted')) action = 'Submit claims';
          
          addTableRow([status, count.toString(), pct + '%', action], [60, 35, 35, 50]);
        });

      // Revenue leakage analysis
      if (data.financials.estimatedLeakage > 0) {
        y += 5;
        addSubSection('Revenue Leakage Analysis');
        const leakagePct = (data.financials.estimatedLeakage / data.financials.totalAmountPaid * 100).toFixed(2);
        addParagraph(`Estimated leakage of $${data.financials.estimatedLeakage.toLocaleString()} represents ${leakagePct}% of total revenue. This may be due to:`);
        addBullet('Services rendered but not billed');
        addBullet('Under-billing (incorrect procedure codes or units)');
        addBullet('Claim denials not resubmitted');
        addBullet('Authorization issues preventing billing');
      }

      // ============================================
      // SECTION 7: COMPLETE STAFF ROSTER
      // ============================================
      newPage();
      addSectionHeader('7', 'COMPLETE STAFF ROSTER WITH METRICS');
      
      addParagraph('Complete listing of all staff members with their performance metrics for the report period.');
      y += 5;

      addTableRow(['Staff Name', 'Services', 'Manual', 'Missing Time', 'Clean Rate', 'Status'], [50, 22, 22, 28, 28, 30], true, [240, 240, 245]);
      
      data.staffMetrics.forEach(staff => {
        const statusText = staff.penaltyStatus === 'exceeded' ? 'EXCEEDED' : 
                          staff.penaltyStatus === 'risk' ? 'AT RISK' : 'OK';
        addTableRow([
          staff.staffName.substring(0, 25),
          staff.totalServices.toString(),
          staff.manualCorrections.toString(),
          staff.missingTimeIncidents.toString(),
          staff.cleanRate.toFixed(0) + '%',
          statusText
        ], [50, 22, 22, 28, 28, 30]);
      });

      // ============================================
      // SECTION 8: COMPLETE CLIENT ROSTER
      // ============================================
      newPage();
      addSectionHeader('8', 'COMPLETE CLIENT ROSTER WITH UTILIZATION');
      
      addParagraph('Complete listing of all clients with their service utilization for the report period.');
      y += 5;

      addTableRow(['Client Name', 'Program', 'Auth Hrs', 'Used Hrs', 'Util %', 'Revenue'], [50, 25, 22, 22, 22, 39], true, [240, 240, 245]);
      
      // Sort by utilization (over-utilized first)
      const sortedClients = [...data.clientUtilization].sort((a, b) => b.utilizationRate - a.utilizationRate);
      
      sortedClients.forEach(client => {
        addTableRow([
          client.clientName.substring(0, 25),
          client.program || 'N/A',
          client.authorizedHours.toFixed(1),
          client.usedHours.toFixed(1),
          client.utilizationRate.toFixed(0) + '%',
          '$' + client.revenue.toLocaleString()
        ], [50, 25, 22, 22, 22, 39]);
      });

      // ============================================
      // SECTION 9: ALL ALERTS DETAIL
      // ============================================
      newPage();
      addSectionHeader('9', 'ALL ALERTS & ISSUES DETAIL');
      
      addParagraph(`Total of ${data.alerts.length} alerts identified. Below is the complete listing grouped by severity.`);
      y += 5;

      // Group alerts by severity
      const alertsBySeverity: Record<string, Alert[]> = {
        'CRITICAL': [],
        'WARNING': [],
        'CAUTION': [],
        'INFO': []
      };
      
      data.alerts.forEach(alert => {
        const severity = alert.type || 'INFO';
        if (!alertsBySeverity[severity]) alertsBySeverity[severity] = [];
        alertsBySeverity[severity].push(alert);
      });

      Object.entries(alertsBySeverity).forEach(([severity, alerts]) => {
        if (alerts.length === 0) return;
        
        checkPageBreak(20);
        addSubSection(`${severity} Alerts (${alerts.length})`);
        
        const bgColor = severity === 'CRITICAL' ? [254, 226, 226] : 
                       severity === 'WARNING' ? [254, 249, 195] : 
                       severity === 'CAUTION' ? [254, 243, 199] : [241, 245, 249];
        
        addTableRow(['Description', 'Staff', 'Client', 'Category'], [70, 40, 40, 30], true, bgColor);
        
        alerts.forEach(alert => {
          addTableRow([
            alert.message.substring(0, 35),
            (alert.staffName || 'N/A').substring(0, 20),
            (alert.clientName || 'N/A').substring(0, 20),
            (alert.category || 'General').substring(0, 15)
          ], [70, 40, 40, 30]);
        });
        y += 5;
      });

      // ============================================
      // SECTION 10: AUDIT FINDINGS
      // ============================================
      newPage();
      addSectionHeader('10', 'AUDIT FINDINGS & REQUIRED ACTIONS');
      
      addParagraph('Based on the analysis above, the following findings require attention. Items are prioritized by severity and regulatory impact.');
      y += 8;

      const findings: { severity: string; finding: string; action: string; deadline: string }[] = [];

      // EVV compliance
      if (evvRate < 90) {
        findings.push({
          severity: 'CRITICAL',
          finding: `EVV compliance rate is ${evvRate.toFixed(1)}%, below the required 90% threshold per COMAR 10.09.36.06`,
          action: 'Implement immediate corrective action plan. Retrain staff on EVV requirements. Review technical barriers.',
          deadline: 'Immediate'
        });
      }

      // Manual entries
      const staffWith5Plus = sortedManualStaff.filter(([_, info]) => info.count >= 5);
      if (staffWith5Plus.length > 0) {
        findings.push({
          severity: 'HIGH',
          finding: `${staffWith5Plus.length} staff member(s) have 5 or more manual entries, indicating potential EVV non-compliance`,
          action: 'Schedule individual meetings with identified staff. Assess training needs. Document reasons for manual entries.',
          deadline: 'Within 7 days'
        });
      }

      // Duplicates
      if (duplicates.length > 0) {
        findings.push({
          severity: 'CRITICAL',
          finding: `${duplicates.length} potential duplicate clock-in instances detected - possible billing fraud or system error`,
          action: 'Immediately investigate each duplicate. Determine if billing adjustments needed. Report findings to compliance officer.',
          deadline: 'Immediate'
        });
      }

      // Over-utilization
      if (overUtilized.length > 0) {
        findings.push({
          severity: 'HIGH',
          finding: `${overUtilized.length} client(s) have received services exceeding their authorization`,
          action: 'Review each case for authorization updates. Submit prior authorization requests. Adjust billing if needed.',
          deadline: 'Within 5 days'
        });
      }

      // Missing time
      if (servicesMissingTime.length > data.services.length * 0.05) {
        findings.push({
          severity: 'MEDIUM',
          finding: `${servicesMissingTime.length} services (${((servicesMissingTime.length / data.services.length) * 100).toFixed(1)}%) are missing time documentation`,
          action: 'Contact staff to obtain missing time records. Update service documentation. Implement time entry validation.',
          deadline: 'Within 14 days'
        });
      }

      // Revenue leakage
      if (data.financials.estimatedLeakage > 1000) {
        findings.push({
          severity: 'MEDIUM',
          finding: `Estimated revenue leakage of $${data.financials.estimatedLeakage.toLocaleString()} identified`,
          action: 'Audit unbilled services. Review claim denials. Implement billing reconciliation process.',
          deadline: 'Within 30 days'
        });
      }

      // Pending claims
      if (data.financials.claimsPending > data.services.length * 0.2) {
        findings.push({
          severity: 'MEDIUM',
          finding: `High volume of pending claims (${data.financials.claimsPending}) affecting cash flow`,
          action: 'Follow up with payer on pending claims. Review for submission errors. Escalate aged claims.',
          deadline: 'Within 14 days'
        });
      }

      if (findings.length === 0) {
        findings.push({
          severity: 'INFO',
          finding: 'No significant compliance issues identified in this review period',
          action: 'Continue current practices. Maintain monitoring and documentation standards.',
          deadline: 'Ongoing'
        });
      }

      // Display findings
      findings.forEach((f, i) => {
        checkPageBreak(35);
        
        const bgColor = f.severity === 'CRITICAL' ? [254, 226, 226] : 
                       f.severity === 'HIGH' ? [254, 243, 199] : 
                       f.severity === 'MEDIUM' ? [254, 249, 195] : [220, 252, 231];
        
        pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        pdf.roundedRect(margin, y - 3, contentWidth, 28, 2, 2, 'F');
        
        pdf.setFontSize(10);
        pdf.setFont('times', 'bold');
        pdf.text(`Finding #${i + 1} [${f.severity}]`, margin + 3, y + 2);
        
        pdf.setFontSize(9);
        pdf.setFont('times', 'normal');
        const findingLines = pdf.splitTextToSize(f.finding, contentWidth - 10);
        pdf.text(findingLines, margin + 3, y + 8);
        
        pdf.setFont('times', 'italic');
        pdf.text(`Action: ${f.action.substring(0, 90)}`, margin + 3, y + 18);
        pdf.text(`Deadline: ${f.deadline}`, margin + 3, y + 23);
        
        y += 32;
      });

      // ============================================
      // SECTION 11: RECOMMENDATIONS
      // ============================================
      newPage();
      addSectionHeader('11', 'RECOMMENDATIONS FOR IMPROVEMENT');
      
      addParagraph('Based on the comprehensive analysis, the following recommendations are provided to improve operations, ensure compliance, and maximize revenue.');
      y += 8;

      const recommendations = [
        {
          title: 'EVV Compliance',
          items: [
            'Conduct monthly EVV compliance reviews',
            'Implement real-time EVV monitoring dashboard',
            'Provide quarterly EVV refresher training for all staff',
            'Document and address technical barriers to EVV use',
            'Establish EVV compliance as a performance metric'
          ]
        },
        {
          title: 'Staff Performance',
          items: [
            'Implement progressive discipline for repeated manual entries',
            'Recognize and reward staff with high clean rates',
            'Conduct root cause analysis for staff with compliance issues',
            'Provide one-on-one coaching for at-risk staff',
            'Include EVV compliance in annual performance reviews'
          ]
        },
        {
          title: 'Client Services',
          items: [
            'Review authorizations monthly for adequacy',
            'Conduct outreach for under-utilized clients',
            'Implement authorization expiration alerts',
            'Document reasons for service variances',
            'Coordinate with case managers on utilization concerns'
          ]
        },
        {
          title: 'Billing & Revenue',
          items: [
            'Implement weekly billing reconciliation',
            'Follow up on claims pending over 30 days',
            'Review and resubmit denied claims promptly',
            'Audit procedure codes for accuracy',
            'Track and address common denial reasons'
          ]
        },
        {
          title: 'Documentation',
          items: [
            'Require service notes for all visits',
            'Implement documentation audits',
            'Provide documentation training',
            'Use standardized note templates',
            'Review documentation during supervision'
          ]
        }
      ];

      recommendations.forEach(rec => {
        checkPageBreak(40);
        addSubSection(rec.title);
        rec.items.forEach(item => {
          addBullet(item);
        });
        y += 5;
      });

      // ============================================
      // FINAL PAGE - SIGNATURE/ATTESTATION
      // ============================================
      newPage();
      pdf.setFontSize(14);
      pdf.setFont('times', 'bold');
      pdf.text('Report Attestation', pageWidth / 2, y, { align: 'center' });
      y += 15;

      addParagraph('This report has been generated based on the data provided in the source file. The accuracy of this report depends on the accuracy and completeness of the source data.');
      y += 10;

      addParagraph('By reviewing this report, the agency acknowledges:');
      addBullet('The findings and recommendations have been reviewed');
      addBullet('Action plans will be developed for identified issues');
      addBullet('Follow-up reviews will be conducted as needed');
      y += 15;

      // Signature lines
      pdf.setDrawColor(0, 0, 0);
      pdf.line(margin, y + 20, margin + 70, y + 20);
      pdf.line(margin + 90, y + 20, margin + 130, y + 20);
      
      pdf.setFontSize(9);
      pdf.text('Reviewed By', margin, y + 25);
      pdf.text('Date', margin + 90, y + 25);
      
      y += 40;
      pdf.line(margin, y, margin + 70, y);
      pdf.line(margin + 90, y, margin + 130, y);
      
      pdf.text('Administrator/Compliance Officer', margin, y + 5);
      pdf.text('Date', margin + 90, y + 5);

      // Final footer
      addFooter();

      // Save the PDF
      const fileName = `LTSS_Compliance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Failed to generate audit report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={generateAuditReport}
      disabled={isGenerating || data.services.length === 0}
      className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 
               disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-colors"
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Generating...
        </>
      ) : (
        <>
          <FileCheck className="w-4 h-4" />
          Audit Report
        </>
      )}
    </button>
  );
}
